import { useEffect, useRef, useState } from 'react';
import type { FrameSource, PoseSample } from '../engine/source';

/**
 * Starts a frame source and exposes the latest sample. `onFrame` sees every
 * frame; the returned sample state updates at most `uiHz` times per second.
 */
export function useLiveFrames(source: FrameSource | null, onFrame?: (s: PoseSample) => void, uiHz = 12) {
  const [sample, setSample] = useState<PoseSample | null>(null);
  const cb = useRef(onFrame);
  cb.current = onFrame;
  const latest = useRef<PoseSample | null>(null);
  useEffect(() => {
    if (!source) return;
    let lastUi = 0;
    source.start((s) => {
      latest.current = s;
      cb.current?.(s);
      if (s.t - lastUi > 1000 / uiHz) {
        lastUi = s.t;
        setSample(s);
      }
    });
    return () => source.stop();
  }, [source, uiHz]);
  return { sample, latest };
}

/** True when the viewport is portrait (setup asks for landscape). */
export function usePortrait() {
  const q = () => window.innerHeight > window.innerWidth;
  const [portrait, setPortrait] = useState(q);
  useEffect(() => {
    const on = () => setPortrait(q());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return portrait;
}
